/**
 * Lightweight MCP (Model Context Protocol) Streamable HTTP client.
 *
 * Handles the JSON-RPC 2.0 handshake, tool discovery, and tool invocation
 * against a single MCP server over HTTP.
 */

export interface McpToolDef {
  name: string;
  description: string;
  inputSchema: {
    type: "object";
    properties: Record<string, any>;
    required?: string[];
  };
}

export type TokenProvider = () => Promise<string>;

export class McpClient {
  private readonly serverUrl: string;
  private readonly getAccessToken?: TokenProvider;
  private sessionId: string | null = null;
  private nextId = 1;

  constructor(serverUrl: string, getAccessToken?: TokenProvider) {
    // Ensure trailing slash is stripped so we POST to the base URL
    this.serverUrl = serverUrl.replace(/\/+$/, "");
    this.getAccessToken = getAccessToken;
  }

  /** Perform the MCP initialize handshake (initialize + notifications/initialized). */
  async initialize(): Promise<void> {
    await this.post({
      jsonrpc: "2.0",
      id: this.nextId++,
      method: "initialize",
      params: {
        protocolVersion: "2025-03-26",
        capabilities: {},
        clientInfo: { name: "tc-agentic-extension", version: "1.0.0" },
      },
    });

    // Send the initialized notification (fire-and-forget, 202 expected)
    await this.post(
      { jsonrpc: "2.0", method: "notifications/initialized" },
      true,
    );
  }

  /** Discover all tools the server exposes. Handles pagination via nextCursor. */
  async listTools(): Promise<McpToolDef[]> {
    const tools: McpToolDef[] = [];
    let cursor: string | undefined;

    do {
      const params: Record<string, unknown> = {};
      if (cursor) params.cursor = cursor;

      const res = await this.post({
        jsonrpc: "2.0",
        id: this.nextId++,
        method: "tools/list",
        params,
      });

      if (res.error) {
        throw new Error(
          `tools/list failed: ${res.error.message ?? JSON.stringify(res.error)}`,
        );
      }

      const result = res.result as {
        tools: McpToolDef[];
        nextCursor?: string;
      };
      tools.push(...result.tools);
      cursor = result.nextCursor;
    } while (cursor);

    return tools;
  }

  /** Invoke a tool by name with the given arguments. Returns the text content. */
  async callTool(name: string, args: Record<string, unknown>): Promise<string> {
    const res = await this.post({
      jsonrpc: "2.0",
      id: this.nextId++,
      method: "tools/call",
      params: { name, arguments: args },
    });

    if (res.error) {
      throw new Error(
        `tools/call ${name} failed: ${res.error.message ?? JSON.stringify(res.error)}`,
      );
    }

    const result = res.result as {
      content: Array<{ type: string; text?: string }>;
      isError?: boolean;
    };

    if (result.isError) {
      const errText = result.content
        .map((c) => c.text ?? "")
        .filter(Boolean)
        .join("\n");
      throw new Error(`MCP tool "${name}" returned error: ${errText}`);
    }

    // Concatenate all text content items
    return result.content
      .filter((c) => c.type === "text" && c.text)
      .map((c) => c.text!)
      .join("\n");
  }

  // ── internals ───────────────────────────────────────────────────────

  /**
   * POST a JSON-RPC message to the MCP server.
   *
   * @param body      The JSON-RPC request or notification
   * @param isNotification  If true, expect 202 with no body
   */
  private async post(body: unknown, isNotification = false): Promise<any> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json, text/event-stream",
    };
    if (this.sessionId) {
      headers["Mcp-Session-Id"] = this.sessionId;
    }
    if (this.getAccessToken) {
      const token = await this.getAccessToken();
      headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(this.serverUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    // Capture session ID from any response
    const sid = res.headers.get("Mcp-Session-Id");
    if (sid) this.sessionId = sid;

    if (isNotification) return {};

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`MCP HTTP ${res.status}: ${text}`);
    }

    const contentType = res.headers.get("Content-Type") ?? "";

    // Standard JSON response
    if (contentType.includes("application/json")) {
      return res.json();
    }

    // SSE response — extract the JSON-RPC message matching our request id
    if (contentType.includes("text/event-stream")) {
      return this.parseSseResponse(await res.text(), body);
    }

    // Fallback: try to parse as JSON
    return res.json();
  }

  /**
   * Parse a text/event-stream body and return the JSON-RPC response whose
   * `id` matches the request.
   */
  private parseSseResponse(raw: string, request: any): any {
    const requestId = (request as any)?.id;
    const lines = raw.split("\n");

    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const data = line.slice("data:".length).trim();
      if (!data || data === "[DONE]") continue;

      try {
        const parsed = JSON.parse(data);
        // Return the message whose id matches, or the first valid one
        if (requestId == null || parsed.id === requestId) {
          return parsed;
        }
      } catch {
        // Skip malformed lines
      }
    }

    throw new Error("No matching JSON-RPC response found in SSE stream");
  }
}
